import {
	ErrorModal,
	getMomentTz,
	TimeService,
	AssetTreePicker,
} from "@dexteel/mesf-core";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	type DialogProps,
	DialogTitle,
	TextField,
	type Theme,
} from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/lab/Alert";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash-es";
import { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { CraftPicker } from "../../../shared/work-orders-modals/components/picker/craftPicker";
import { useTemplatesContext } from "../context/templatesContext";
import type { ParametersNewTemplate } from "../models/ParametersNewTemplate";
import type { Template } from "../models/Template";
import { upsertTemplate } from "../repositories/TemplatesRepositorie";
import {
	getTemplateRecipients,
	setTemplateRecipients,
} from "@setup/configurations/email-recipients/repositories/EmailRecipientsRepository";
import type { TemplateRecipient } from "@setup/configurations/email-recipients/models/EmailRecipient";
import { NotifyRecipientsField } from "./recipients-picker/NotifyRecipientsField";
import { ScheduleCodePicker } from "./picker/ScheduleCodePicker";
import cronstrue from "cronstrue";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import { DateTimePicker } from "@mui/x-date-pickers";
import { TableTemplateResoures } from "./template-resources/table-template-resoures";

const moment = getMomentTz();

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			"& .MuiFormLabel-root": {
				transform: "translate(14px, -6px) scale(0.75) ",
				backgroundColor: "#FFFF",
				transition: "none !important",
				left: -3.5,
				padding: "0 5px",
			},
			"& .MuiInputAdornment-root": {
				marginTop: "-8px",
			},
			"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
				borderColor: "#3F51B5",
			},
			"& .MuiFormLabel-root.Mui-focused": {
				color: "#3F51B5",
			},
		},
		assetPicker: {
			"& .MuiGrid-item": {
				width: "500px",
			},
		},
		inputEditable: {
			"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
				border: "2px solid #3F51B5",
			},
		},
		title: {
			fontSize: 15,
			margin: "3px 0",
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
		infoLabel: {
			fontSize: 12,
			color: "#757575",
		},
		reponsive: {
			[theme.breakpoints.up("md")]: {
				padding: "0 !important",
			},
		},
		reponsiveTwo: {
			[theme.breakpoints.up("md")]: {
				padding: "0 10px 0 0 !important",
			},
		},
		disabledDatePicker: {
			"& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
				{
					borderWidth: "1px !important",
					borderColor: "rgba(0, 0, 0, 0.23)",
				},
			"& .MuiInputBase-input.Mui-disabled": {
				color: "rgba(0, 0, 0, 0.38)",
			},
			"& .MuiOutlinedInput-root:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline":
				{
					border: "2px solid #3F51B5",
				},
		},
	}),
);

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	/** Template whose values pre-fill the form; the result is still a new template. */
	duplicateSource?: Template | null;
	/** Asset a new (non-duplicate) template starts on; defaults to 1. */
	initialAssetId?: number | null;
};

export const CreateTemplate = ({
	show,
	onHide,
	duplicateSource,
	initialAssetId,
}: Props) => {
	const {
		state: {
			INITIAL_VALUE_TEMPLATE: { AssetId },
		},
		state: { INITIAL_VALUE_TEMPLATE },
		state,
		actions: { setAssetId, setRowSelectedTemplate },
	} = useTemplatesContext();

	const [open, setOpen] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const classes = useStyles();
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("md");
	const [error, setError] = useState<string>("");
	const [editTemplateError, setEditTemplateError] = useState<string>("");
	const [enableScheduleStart, setEnableScheduleStart] = useState<boolean>(true);
	const [newMode, setNewMode] = useState(true);
	const [assetChanged, setAssetChanged] = useState(false);
	const [cronExpression, setCronExpression] = useState("");
	const [showResourcesDialog, setShowResourcesDialog] = useState(false);
	const [openTemplateResources, setOpenTemplateResources] = useState(false);
	const [createdTemplateId, setCreatedTemplateId] = useState<number | null>(
		null,
	);
	const isDuplicate = !!duplicateSource;
	const sourceTemplateId = duplicateSource?.TemplateId ?? null;
	const [recipientsSeeded, setRecipientsSeeded] = useState(false);

	const sourceRecipients = useQuery({
		queryKey: ["templateRecipients", sourceTemplateId],
		queryFn: () => getTemplateRecipients(sourceTemplateId),
		enabled: show && sourceTemplateId !== null,
	});

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		formState: { errors },
		watch,
	} = useForm<ParametersNewTemplate>({ defaultValues: INITIAL_VALUE_TEMPLATE });

	const description = watch("Description");
	const schedulingPolicyCode = watch("SchedulingPolicyCode");

	const changeSearchAsset = (value: number | null) => {
		if (value !== null) {
			setValue("AssetId", value);
			setAssetId(value);
			setAssetChanged(true);
		}
	};

	const handleCancel = () => {
		onHide(false);
		setNewMode(true);
	};

	const onSubmit: SubmitHandler<ParametersNewTemplate> = async (data: any) => {
		setIsSubmitLoading(true);

		const submitData = {
			...data,
			CronExpression: data.SchedulingPolicyCode === "P" ? cronExpression : "",
		};

		const res = await upsertTemplate(submitData);
		if (res.ok) {
			setNewMode(false);
			const templateId = res.data?.tables?.[0]?.rows?.[0]?.TemplateId;
			setCreatedTemplateId(templateId);

			// The template has to exist before anybody can be attached to it, so the picks made in
			// the form are saved here rather than travelling with the template itself.
			if (templateId) {
				const recipientsResp = await setTemplateRecipients(
					templateId,
					submitData.RecipientIds ?? [],
				);
				if (!recipientsResp.ok) {
					setEditTemplateError(recipientsResp.message);
					setIsSubmitLoading(false);
					return;
				}
			}

			setRowSelectedTemplate({
				...submitData,
				TemplateId: templateId,
			});

			setAssetChanged(false);
			setIsSubmitLoading(false);
			setShowResourcesDialog(true);
		} else {
			setEditTemplateError(res.message);
			setIsSubmitLoading(false);
		}
	};

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};

	const handleOpenTemplateResources = () => {
		setShowResourcesDialog(false);
		setOpenTemplateResources(true);
	};

	const handleCloseTemplateResources = (shouldUpdate: boolean) => {
		setOpenTemplateResources(false);
		onHide(true);
		setOpen(true);
	};

	const handleSkipResources = () => {
		setShowResourcesDialog(false);
		onHide(true);
		setOpen(true);
	};

	const handleScheduleStartChange = (date: Date) => {
		setValue("ScheduleStart", date);
	};

	useEffect(() => {
		if (schedulingPolicyCode === "P") {
			setEnableScheduleStart(false);
		} else {
			setEnableScheduleStart(true);
		}
	}, [schedulingPolicyCode]);

	useEffect(() => {
		// Only on open: resetting on close flashes the defaults while the dialog fades out
		if (!show) return;
		// Plain reset(): passing values would also replace the defaults the next New Template opens with
		reset();
		setCronExpression("");
		setIsModalOpen(false);

		if (show && duplicateSource) {
			const source = duplicateSource;
			setAssetId(source.AssetId);
			setValue("AssetId", source.AssetId as number);
			setValue("AssetName", source.AssetName);
			setValue("Title", `Copy of ${source.Title}`.slice(0, 200));
			setValue("CraftId", source.CraftId);
			setValue("TypeCode", source.TypeCode ?? "PM");
			// The grid row is already in days (useSearchData divides the shifts on load)
			setValue("selectedPeriodDays", source.PeriodShifts);
			setValue("selectedExpectedDays", source.ExpectedShifts);
			setValue("selectedWarningDays", source.WarningShifts);
			setValue("PlannedHours", source.PlannedHours);
			setValue("DownHours", source.DownHours);
			setValue("NumberOfWorkers", source.NumberOfWorkers);
			setValue("Description", source.Description);
			// The repository sends any truthy value as "D", so only a real "D" may pass through
			setValue(
				"DowntimeClassCode",
				source.DowntimeClassCode === "D" ? "D" : null,
			);
			setValue("SchedulingPolicyCode", source.SchedulingPolicyCode);
			setValue("ScheduleStart", source.ScheduleStart);
			setValue("NotifyOnGeneration", !!source.NotifyOnGeneration);
			setValue("RecipientIds", []);
			setCronExpression(source.CronExpression ?? "");
			return;
		}

		const startAssetId = initialAssetId ?? 1;
		setAssetId(startAssetId);
		setValue("AssetId", startAssetId);
		setValue("ScheduleStart", moment().startOf("day").toDate());
	}, [show, duplicateSource, initialAssetId]);

	// The copy takes the source's recipients once the fresh list arrives; Save waits for it so the
	// new template is never created with nobody on it
	useEffect(() => {
		if (!show) {
			setRecipientsSeeded(false);
			return;
		}
		if (
			sourceTemplateId === null ||
			recipientsSeeded ||
			sourceRecipients.isFetching ||
			!sourceRecipients.data
		)
			return;
		if (!sourceRecipients.data.ok) {
			setError(sourceRecipients.data.message);
			return;
		}
		const rows = get(
			sourceRecipients.data,
			"data.tables[0].rows",
			[],
		) as TemplateRecipient[];
		setValue(
			"RecipientIds",
			rows.filter((r) => r.IsSelected).map((r) => r.RecipientId as number),
		);
		setRecipientsSeeded(true);
	}, [
		show,
		sourceTemplateId,
		recipientsSeeded,
		sourceRecipients.isFetching,
		sourceRecipients.data,
	]);

	return (
		<>
			<Grid2 container>
				<Grid2>
					<Dialog
						fullWidth={fullWidth}
						maxWidth={maxWidth}
						open={show}
						onClose={() => handleCancel()}
					>
						<form
							onSubmit={handleSubmit(onSubmit)}
							style={{ paddingTop: "8px" }}
						>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<DialogTitle
									style={{
										padding: "0px 24px 0px 24px",
										margin: "0px",
										width: "100%",
									}}
								>
									<Grid2 size={{ md: 12, xs: 12 }}>
										<h1
											style={{
												fontSize: "1.7em",
												margin: "0px",
												padding: "8px 0px",
											}}
										>
											{isDuplicate ? "DUPLICATE TEMPLATE" : "NEW TEMPLATE"}
										</h1>
									</Grid2>
								</DialogTitle>
							</Grid2>
							<DialogContent
								style={{ padding: "0 24px", overflow: "visible" }}
								dividers
							>
								<h6
									className={classes.title}
									id="contained-modal-title-vcenter"
									style={{ marginBottom: 0 }}
								>
									TEMPLATE TITLE
								</h6>
								<Grid2 container spacing={1}>
									<Grid2 size={{ md: 12, xs: 12 }}>
										<Controller
											name="Title"
											control={control}
											rules={{
												validate: (value) => value !== "",
											}}
											render={({ field: { onChange, value } }) => (
												<TextField
													className={classes.inputEditable}
													label="Title"
													variant="outlined"
													fullWidth
													multiline
													maxRows={1}
													onKeyDown={(e) => {
														if (e.key === "Enter") e.preventDefault();
													}}
													error={!!errors.Title}
													placeholder="Title of the new template"
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
												/>
											)}
										/>
										{errors.Title?.type === "required" && (
											<span className={classes.errorLabel}>
												Title is required
											</span>
										)}
									</Grid2>
								</Grid2>
								<h6
									className={classes.title}
									style={{ margin: "5px 0 -5px" }}
									id="contained-modal-title-vcenter"
								>
									MAIN PROPERTIES
								</h6>
								<Grid2 container>
									<Grid2
										container
										style={{ marginTop: 10, padding: 0 }}
										className={classes.assetPicker}
										size={{ md: 12, xs: 12 }}
									>
										<Grid2
											style={{ padding: "0 10px 0 0" }}
											className={classes.inputEditable}
											size={{ md: 6, xs: 12 }}
										>
											<Controller
												name="AssetId"
												control={control}
												render={({ field: { value } }) => (
													<AssetTreePicker
														label="Asset"
														value={value || 0}
														onChange={changeSearchAsset}
													/>
												)}
											/>
										</Grid2>
										<Grid2
											style={{ padding: "0 10px 0 0" }}
											size={{ md: 4, xs: 6 }}
										>
											<Controller
												name="CraftId"
												control={control}
												render={({ field: { onChange, value } }) => (
													<CraftPicker
														value={value as number}
														onChange={onChange}
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ md: 2, xs: 6 }}>
											<Controller
												name="TypeCode"
												control={control}
												render={({}) => (
													<TextField
														label="Type"
														variant="outlined"
														disabled
														value={state.INITIAL_VALUE_TEMPLATE.TypeCode}
														fullWidth
														margin="dense"
														autoComplete="off"
														InputLabelProps={{
															style: {
																fontSize: "14px",
															},
														}}
													/>
												)}
											/>
										</Grid2>
									</Grid2>
								</Grid2>
								<Controller
									name="NotifyOnGeneration"
									control={control}
									render={({ field: { onChange, value } }) => (
										<NotifyRecipientsField
											checked={!!value}
											onCheckedChange={onChange}
											recipientIds={watch("RecipientIds") ?? []}
											onRecipientIdsChange={(ids) =>
												setValue("RecipientIds", ids)
											}
											templateId={null}
											fieldClassName={`${classes.root} ${classes.inputEditable}`}
										/>
									)}
								/>
								<h6
									className={classes.title}
									id="contained-modal-title-vcenter"
								>
									SCHEDULING
								</h6>
								<Grid2 container size={{ md: 12, xs: 12 }}>
									<Grid2
										style={{ padding: "0 10px 0 0" }}
										size={{ md: 3, xs: 6 }}
									>
										<Controller
											name="SchedulingPolicyCode"
											control={control}
											render={({ field: { onChange, value } }) => (
												<ScheduleCodePicker
													value={value}
													onChange={(newValue: string) => {
														onChange(newValue);
													}}
													cronExpression={cronExpression}
													onCronExpressionChange={setCronExpression}
													isModalOpen={isModalOpen}
													setIsModalOpen={setIsModalOpen}
													hideScheduleStart={setEnableScheduleStart}
													onScheduleStartChange={handleScheduleStartChange}
												/>
											)}
										/>
									</Grid2>

									{enableScheduleStart && (
										<Grid2
											style={{ padding: "0 10px 0 0" }}
											size={{ md: 2, xs: 6 }}
										>
											<Controller
												name="selectedPeriodDays"
												control={control}
												rules={{
													validate: (value) => value !== 0,
												}}
												render={({ field: { onChange, value } }) => (
													<TextField
														className={`${classes.root} ${classes.inputEditable}`}
														type="number"
														label="Repeat every"
														variant="outlined"
														fullWidth
														margin="dense"
														autoComplete="off"
														value={value}
														onChange={(e) => onChange(e)}
														InputLabelProps={{
															style: {
																fontSize: "14px",
															},
														}}
													/>
												)}
											/>
										</Grid2>
									)}

									<Grid2
										style={{ padding: "0 20px 0 10px" }}
										className={classes.root}
										size={{
											md: enableScheduleStart ? 3 : 5,
											xs: enableScheduleStart ? 6 : 6,
										}}
									>
										<Controller
											name="ScheduleStart"
											control={control}
											render={({ field }) => (
												<>
													<DateTimePicker
														label="Schedule Start"
														timezone={
															enableScheduleStart
																? TimeService.getInstance().getServerTimeZone()
																: "UTC"
														}
														format={
															enableScheduleStart
																? "MM/DD/YYYY HH:mm"
																: "MM/DD/YYYY"
														}
														timeSteps={{ minutes: 1 }}
														value={moment(field.value) as any}
														onChange={field.onChange}
														inputRef={field.ref}
														disabled={schedulingPolicyCode === "P"}
														slotProps={{
															textField: {
																fullWidth: true,
																variant: "outlined",
																margin: "dense",
																size: "small",
																error: !!errors.ScheduleStart,
																className: classes.disabledDatePicker,
															},
														}}
													/>
													{errors.ScheduleStart && (
														<span className={classes.errorLabel}>
															{errors.ScheduleStart.message}
														</span>
													)}
												</>
											)}
										/>
									</Grid2>

									<Grid2
										className={classes.reponsiveTwo}
										size={{ md: 2, xs: 6 }}
									>
										<Controller
											name="selectedExpectedDays"
											control={control}
											render={({ field: { onChange, value } }) => (
												<TextField
													className={`${classes.root} ${classes.inputEditable}`}
													type="number"
													label="Expected duration"
													variant="outlined"
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
												/>
											)}
										/>
									</Grid2>

									<Grid2 style={{ padding: "0" }} size={{ md: 2, xs: 6 }}>
										<Controller
											name="selectedWarningDays"
											control={control}
											render={({ field: { onChange, value } }) => (
												<TextField
													className={`${classes.root} ${classes.inputEditable}`}
													type="number"
													label="Warn after"
													variant="outlined"
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
													InputLabelProps={{
														style: {
															fontSize: "14px",
														},
													}}
												/>
											)}
										/>
									</Grid2>
									{schedulingPolicyCode === "P" && (
										<Grid2 size={{ md: 12, xs: 12 }}>
											<TextField
												label="Schedule"
												variant="outlined"
												fullWidth
												margin="dense"
												placeholder="Choose your schedule"
												value={
													cronExpression.length > 0
														? cronstrue.toString(cronExpression as string)
														: ""
												}
												slotProps={{
													input: {
														readOnly: true,
														endAdornment: (
															<IconButton
																edge="end"
																onClick={() => setIsModalOpen(true)}
																size="small"
															>
																<EditIcon />
															</IconButton>
														),
													},
												}}
											/>
										</Grid2>
									)}
								</Grid2>
								<h6
									className={classes.title}
									id="contained-modal-title-vcenter"
								>
									WORK TIME
								</h6>
								<Grid2 container size={{ md: 12, xs: 12 }}>
									<Grid2
										style={{ padding: "0 10px 0 0" }}
										size={{ md: 4, xs: 4 }}
									>
										<Controller
											name="PlannedHours"
											control={control}
											render={({ field: { onChange, value } }) => (
												<TextField
													className={classes.inputEditable}
													type="number"
													label="Planned Hours"
													variant="outlined"
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
													InputLabelProps={{
														style: {
															fontSize: "14px",
														},
													}}
												/>
											)}
										/>
									</Grid2>
									<Grid2
										className={classes.reponsiveTwo}
										style={{ padding: "0 0 0 0 !important" }}
										size={{ md: 4, xs: 4 }}
									>
										<Controller
											name="DownHours"
											control={control}
											render={({ field: { onChange, value } }) => (
												<TextField
													className={`${classes.root} ${classes.inputEditable}`}
													type="number"
													label="Down time Hours"
													variant="outlined"
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
													InputLabelProps={{
														style: {
															fontSize: "14px",
														},
													}}
												/>
											)}
										/>
									</Grid2>

									<Grid2
										className={classes.reponsive}
										style={{ padding: "0 0 0 10px" }}
										size={{ md: 4, xs: 4 }}
									>
										<Controller
											name="NumberOfWorkers"
											control={control}
											rules={{
												min: 1,
											}}
											render={({ field: { onChange, value } }) => (
												<TextField
													className={classes.inputEditable}
													type="number"
													label="Workers Needed"
													variant="outlined"
													error={!!errors.NumberOfWorkers}
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) =>
														Number.parseInt(e.target.value as any) >= 1 &&
														onChange(e)
													}
													InputLabelProps={{
														style: {
															fontSize: "14px",
														},
													}}
												/>
											)}
										/>
									</Grid2>
								</Grid2>
								<h6
									className={classes.title}
									id="contained-modal-title-vcenter"
								>
									MISC
								</h6>
								<Grid2
									style={{ margin: "5px 0 0", padding: "0px" }}
									size={{ md: 12, xs: 12 }}
								>
									<Controller
										name="Description"
										control={control}
										render={({ field: { value, onChange } }) => (
											<TextField
												className={`${classes.root} ${classes.inputEditable}`}
												style={{ margin: "8px 0" }}
												size="medium"
												maxRows={5}
												label="Description"
												multiline
												variant="outlined"
												fullWidth
												autoComplete="off"
												value={value}
												onChange={(evt) => {
													if (evt.target.value.length <= 400) onChange(evt);
												}}
											/>
										)}
									/>
									<Grid2 container style={{ position: "relative" }}>
										<Grid2 style={{ padding: 0 }}>
											<span
												className={classes.infoLabel}
												style={
													(description?.length as number) >= 1 &&
													(description?.length as number) !== 400
														? {
																marginLeft: 5,
																visibility: "visible",
																position: "absolute",
																left: 0,
															}
														: { visibility: "hidden" }
												}
											>{`${description?.length}/400 max.`}</span>
											<span
												className={classes.infoLabel}
												style={
													(description?.length as number) == 400
														? {
																marginLeft: 5,
																visibility: "visible",
																position: "absolute",
																left: 0,
															}
														: { visibility: "hidden" }
												}
											>
												Max. 400
											</span>
										</Grid2>
									</Grid2>
								</Grid2>
							</DialogContent>
							<DialogActions style={{ padding: "0", margin: "10px 0" }}>
								<Grid2
									container
									spacing={2}
									justifyContent="flex-end"
									style={{ margin: "0 15px" }}
								>
									<Grid2
										spacing={2}
										style={{
											display: "flex",
											alignItems: "center",
											marginRight: "auto",
										}}
										title={
											assetChanged
												? "You need to save to be able to manage resources"
												: ""
										}
										size={{ md: 4, xs: 4 }}
									></Grid2>
									<Grid2 style={{ margin: 0 }} size={{ md: 3, xs: 12 }}>
										<Button
											fullWidth
											variant="contained"
											color="inherit"
											onClick={handleCancel}
										>
											Cancel
										</Button>
									</Grid2>
									<Grid2 style={{ margin: 0 }} size={{ md: 3, xs: 12 }}>
										<Button
											fullWidth
											startIcon={
												isSubmitLoading && <CircularProgress size="1rem" />
											}
											disabled={
												isSubmitLoading ||
												(isDuplicate && !recipientsSeeded) ||
												(schedulingPolicyCode === "P" &&
													(!cronExpression ||
														cronExpression.trim().length === 0))
											}
											variant="contained"
											color="primary"
											type="submit"
										>
											Save
										</Button>
									</Grid2>
								</Grid2>
							</DialogActions>
						</form>
					</Dialog>
				</Grid2>
			</Grid2>

			<Dialog
				open={showResourcesDialog}
				onClose={handleSkipResources}
				aria-labelledby="add-resources-dialog-title"
			>
				<DialogTitle id="add-resources-dialog-title">
					Add Resources to Template
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						The template was created successfully. Would you like to add
						resources to this template now?
					</DialogContentText>
				</DialogContent>
				<DialogActions style={{ padding: "16px 24px" }}>
					<Button
						variant="contained"
						color="inherit"
						onClick={handleSkipResources}
					>
						CLOSE
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleOpenTemplateResources}
						autoFocus
					>
						ADD RESOURCES
					</Button>
				</DialogActions>
			</Dialog>
			{openTemplateResources && (
				<TableTemplateResoures
					show={openTemplateResources}
					onHide={handleCloseTemplateResources}
					AssetId={watch("AssetId")}
					TemplateId={createdTemplateId}
					newMode={true}
					Title={watch("Title") as string}
					AssetName={(watch("AssetName") as string) || ""}
					TypeCode={state.INITIAL_VALUE_TEMPLATE.TypeCode as string}
					CraftId={watch("CraftId") as number}
				/>
			)}

			<Snackbar
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				open={open}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity="success" onClose={handleClose}>
					The template was created successfully
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={editTemplateError}
				onHide={() => setEditTemplateError("")}
			/>
		</>
	);
};
